import React, { useState, useEffect } from 'react';
import { Typography, Tag, Card, Button, Image, message, Spin, Divider, Avatar, Tooltip, Input, Form, Alert } from 'antd';
import { 
  CopyOutlined, 
  HomeOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  ContactsOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  ShareAltOutlined,
  CheckOutlined,
  LockOutlined,
  SafetyOutlined
} from '@ant-design/icons';

// 引入验证UI样式
import '../styles/share-verification.css';
import { useParams, useNavigate } from 'react-router-dom';
import DB from '../services/dbService';

const { Title, Paragraph } = Typography;

// 将文本中的URL转换为可点击的链接
const renderTextWithLinks = (text) => {
  if (!text) return '';
  
  // URL匹配正则表达式
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // 将文本分割成URL和非URL部分
  const parts = text.split(urlRegex);
  
  // 匹配所有URL
  const urls = text.match(urlRegex) || [];
  
  // 构建结果数组
  const result = [];
  parts.forEach((part, index) => {
    if (urls.includes(part)) {
      // 如果是URL，创建链接
      result.push(
        <a 
          key={index} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 hover:underline"
        >
          {part}
        </a>
      );
    } else if (part) {
      // 如果是普通文本
      result.push(<span key={index}>{part}</span>);
    }
  });
  
  return result;
};

// 共享提示词查看组件
function SharedPromptView() {
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  // 验证码通过表单直接提交，无需单独的状态
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // 设置body的data-page属性，以便于CSS选择器应用
  useEffect(() => {
    document.body.setAttribute('data-page', 'share');
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, []);
  
  // 加载分享的提示词元数据
  useEffect(() => {
    const loadSharedPromptMetadata = async () => {
      if (!shareId) {
        setError('分享链接无效');
        setLoading(false);
        return;
      }
      
      try {
        // 先获取提示词的元数据
        const metadata = await DB.getSharedPromptMetadata(shareId);
        console.log('提示词元数据:', metadata);
        
        if (metadata.exists) {
          setMetadata(metadata);
          
          if (metadata.isLocal) {
            // 如果是本地提示词，直接加载
            const sharedPrompt = await DB.getPromptByShareId(shareId);
            if (sharedPrompt) {
              console.log('加载的本地提示词数据:', sharedPrompt);
              setPrompt(sharedPrompt);
              setVerified(true);
              setError(null);
            } else {
              setError('提示词不存在或已取消分享');
            }
          }
          // 如果不是本地提示词，需要验证码验证，会在界面上展示验证表单
        } else {
          setError('提示词不存在或已取消分享');
        }
      } catch (err) {
        console.error('加载分享提示词元数据失败:', err);
        setError('加载分享提示词失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };
    
    loadSharedPromptMetadata();
  }, [shareId]);
  
  // 验证并获取分享的提示词
  const verifyAndLoadPrompt = async (values) => {
    // 从表单提交的值中获取验证码
    const code = values.verificationCode;
    
    if (!code || code.trim() === '') {
      message.error('请输入验证码');
      return;
    }
    
    setVerifying(true);
    console.log('准备验证分享ID:', shareId, '验证码:', code);
    
    try {
      const sharedPrompt = await DB.verifyAndGetSharedPrompt(shareId, code);
      
      if (sharedPrompt) {
        console.log('验证成功，加载的提示词数据:', sharedPrompt);
        setPrompt(sharedPrompt);
        setVerified(true);
        setError(null);
        message.success('验证成功，已加载提示词');
      } else {
        setError('验证失败，无法获取提示词');
      }
    } catch (err) {
      console.error('验证并获取分享提示词失败:', err);
      setError('验证失败，请检查验证码是否正确');
    } finally {
      setVerifying(false);
    }
  };
  
  // 复制提示词内容到剪贴板
  const handleCopyContent = () => {
    try {
      if (!prompt || !prompt.versions || !prompt.versions[prompt.currentVersionIndex]) {
        message.error('无法复制内容，提示词数据不完整');
        return;
      }
      
      const currentVersion = prompt.versions[prompt.currentVersionIndex];
      
      navigator.clipboard.writeText(currentVersion.content)
        .then(() => {
          setCopySuccess(true);
          message.success('复制成功');
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => {
          console.error('复制失败:', err);
          message.error('复制失败，请手动选择文本复制');
        });
    } catch (error) {
      console.error('复制操作异常:', error);
      message.error('复制失败，请手动选择文本复制');
    }
  };
  
  // 返回主页
  const handleBackHome = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full h-full overflow-y-auto">
        <Spin size="large" tip="正在加载..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen w-full h-full p-4 overflow-y-auto">
        <div className="text-red-500 text-xl mb-4">{error}</div>
        <Button type="primary" icon={<HomeOutlined />} onClick={handleBackHome}>
          返回首页
        </Button>
      </div>
    );
  }
  
  // 需要验证码验证的情况
  if (metadata && !metadata.isLocal && !verified) {
    return (
      <div className="share-verification-container">
        <div className="share-verification-card">
          <div className="decoration-circle decoration-circle-1"></div>
          <div className="decoration-circle decoration-circle-2"></div>
          
          <SafetyOutlined className="verification-icon" />
          
          <h2 className="share-verification-title">验证分享提示词</h2>
          <p className="share-verification-subtitle">
            该提示词需要验证码才能查看，请输入分享者提供的验证码。
          </p>
          
          <Alert
            className="verification-alert"
            message="验证提示"
            description="验证码由分享者提供，如果你没有验证码，请联系分享者获取。"
            type="info"
            showIcon
            icon={<LockOutlined />}
          />
          
          <Form form={form} onFinish={verifyAndLoadPrompt} layout="vertical">
            <div className="verification-input-container">
              <label className="verification-input-label">* 验证码</label>
              <Form.Item 
                name="verificationCode" 
                rules={[{ required: true, message: '请输入验证码' }]}
                noStyle
              >
                <Input 
                  placeholder="请输入六位验证码" 
                  maxLength={6} 
                  className="verification-input"
                  autoFocus
                />
              </Form.Item>
            </div>
            
            <div className="verification-buttons">
              <Button 
                onClick={handleBackHome}
                className="back-button"
                icon={<HomeOutlined />}
              >
                返回首页
              </Button>
              <Button 
                htmlType="submit" 
                className="verify-button"
                loading={verifying}
              >
                {verifying ? <span>验证中...</span> : <span>验证查看</span>}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    );
  }
  
  if (!prompt) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen w-full h-full p-4 overflow-y-auto">
        <div className="text-red-500 text-xl mb-4">提示词不存在或已被删除</div>
        <Button type="primary" icon={<HomeOutlined />} onClick={handleBackHome}>
          返回首页
        </Button>
      </div>
    );
  }
  
  // 获取当前版本
  const currentVersion = prompt.versions[prompt.currentVersionIndex];
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef] dark:from-[#1a1a1a] dark:to-[#2d2d2d] p-4 md:p-8 overflow-y-auto w-full h-full">
      <div className="max-w-4xl mx-auto overflow-y-auto">
        {/* 顶部导航栏 */}
        <div className="flex justify-between items-center mb-8 bg-white/80 dark:bg-black/30 backdrop-blur-md p-4 rounded-xl shadow-sm">
          <Button 
            type="primary" 
            ghost
            icon={<HomeOutlined />} 
            onClick={handleBackHome}
            className="hover:scale-105 transition-transform"
          >
            返回首页
          </Button>
          <div className="flex items-center gap-2">
            <ShareAltOutlined className="text-blue-500" />
            <span className="font-medium">提示词分享页</span>
          </div>
        </div>
        
        {/* 提示词内容卡片 */}
        <Card 
          className="shadow-lg bg-white dark:bg-gray-800 rounded-2xl border-0 mb-6 overflow-visible transition-all duration-300 hover:shadow-xl"
          style={{ boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}
        >
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <Title level={2} className="text-[var(--apple-text-primary)] mb-3 font-bold">
                  {prompt.title}
                </Title>
                
                <div className="flex items-center text-[var(--apple-text-secondary)] mb-4">
                  <Tooltip title="创建时间">
                    <CalendarOutlined className="mr-2 text-blue-500" />
                  </Tooltip>
                  <span>创建于: {formatDate(prompt.createdAt)}</span>
                  {prompt.updatedAt && (
                    <>
                      <Divider type="vertical" className="mx-3" />
                      <Tooltip title="更新时间">
                        <CalendarOutlined className="mr-2 text-green-500" />
                      </Tooltip>
                      <span>更新于: {formatDate(prompt.updatedAt)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mb-6 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <InfoCircleOutlined className="text-amber-500 mr-2" />
                <span className="font-medium text-gray-700 dark:text-gray-300">提示词简介</span>
              </div>
              {prompt.introduction ? (
                <Paragraph className="text-gray-600 dark:text-gray-400 mb-0" ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}>
                  {prompt.introduction}
                </Paragraph>
              ) : (
                <Paragraph className="text-gray-400 italic mb-0">
                  暂无简介
                </Paragraph>
              )}
            </div>
            
            {/* 作者信息 */}
            {(prompt.authorName || prompt.authorContact) && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 mb-6 shadow-inner border border-blue-100 dark:border-blue-800/30">
                <div className="flex items-start">
                  <Avatar 
                    size={48} 
                    icon={<UserOutlined />} 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 shadow-md mr-4"
                  />
                  <div className="flex-1">
                    <div className="flex flex-col">
                      {prompt.authorName && (
                        <div className="mb-3">
                          <div className="text-xs text-blue-500 mb-1 font-medium uppercase tracking-wider">作者</div>
                          <div className="text-lg font-medium text-gray-800 dark:text-gray-200">
                            {prompt.authorName}
                          </div>
                        </div>
                      )}
                      {prompt.authorContact && (
                        <div>
                          <div className="text-xs text-blue-500 mb-1 font-medium uppercase tracking-wider flex items-center">
                            <ContactsOutlined className="mr-1" />
                            联系方式
                          </div>
                          <div className="text-gray-700 dark:text-gray-300 break-all">
                            {renderTextWithLinks(prompt.authorContact)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* 描述已移动到简介部分 */}
          </div>
          
          <Divider className="my-6" />
          
          {/* 提示词内容 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <FileTextOutlined className="text-green-500 mr-2 text-xl" />
                <Title level={4} className="text-gray-800 dark:text-gray-200 m-0 font-medium">
                  提示词内容
                </Title>
              </div>
              <Button 
                type="primary" 
                icon={copySuccess ? <CheckOutlined /> : <CopyOutlined />} 
                onClick={handleCopyContent}
                className={`${copySuccess ? 'bg-green-500' : 'bg-blue-500'} border-0 hover:opacity-90 transition-opacity shadow-md`}
              >
                {copySuccess ? '已复制' : '复制内容'}
              </Button>
            </div>
            
            <div className="whitespace-pre-wrap p-6 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 font-mono text-gray-800 dark:text-gray-200 overflow-x-auto max-h-[500px] overflow-y-auto shadow-inner">
              {renderTextWithLinks(currentVersion.content)}
            </div>
          </div>
          
          {/* 版本信息 */}
          <div className="mb-8 bg-gray-50 dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <InfoCircleOutlined className="text-blue-500 mr-2 text-lg" />
              <Title level={4} className="text-gray-800 dark:text-gray-200 m-0 font-medium">
                版本信息
              </Title>
            </div>
            <div className="flex items-center bg-white dark:bg-gray-700 p-3 rounded-lg shadow-sm">
              <Tag color="blue" className="mr-3 py-1 px-3 text-sm rounded-lg">
                v{currentVersion.versionNumber || `${prompt.currentVersionIndex + 1}.0`}
              </Tag>
              {currentVersion.description ? (
                <div className="text-gray-700 dark:text-gray-300">{currentVersion.description}</div>
              ) : (
                <div className="text-gray-400 italic">暂无版本描述</div>
              )}
            </div>
          </div>
          
          {/* 结果图片区域 */}
          {currentVersion.resultImages && currentVersion.resultImages.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <PictureOutlined className="text-purple-500 mr-2 text-xl" />
                <Title level={4} className="text-gray-800 dark:text-gray-200 m-0 font-medium">
                  运行结果展示
                </Title>
              </div>
              <div className="image-gallery grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 p-5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner">
                {currentVersion.resultImages.map((image, index) => (
                  <div key={`img-${index}`} className="image-item transition-transform hover:scale-105 duration-300">
                    <Image 
                      src={image.url} 
                      alt={`结果图片 ${index + 1}`} 
                      className="object-cover rounded-lg shadow-md"
                      preview={{ 
                        mask: <div className="flex items-center justify-center">
                          <PictureOutlined className="mr-1" /> 点击查看
                        </div> 
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 分享信息 */}
          <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center mb-3">
                <ShareAltOutlined className="text-blue-500 mr-2" />
                <span className="text-gray-500 dark:text-gray-400">由 PromptPro 提示词管理工具提供</span>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-full text-blue-600 dark:text-blue-300 text-sm font-medium shadow-sm">
                分享于: {formatDate(new Date().toISOString())}
              </div>
              <div className="mt-4 flex gap-3">
                <Button type="link" size="small" onClick={handleBackHome}>
                  访问首页
                </Button>
                <Button type="link" size="small" onClick={handleCopyContent}>
                  复制内容
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default SharedPromptView;
